/**
 * tabs: the Dashboard's tab-routing contract. PR-4's Building-page mechanism
 * (`pages/Portal/BuildingDetail/tabs.ts` is the reference), in two halves:
 * `validateSearch` on the file route drops an unrecognised `?tab=` to
 * `undefined`; `readDashboardSearch` turns no/unknown tab into the default.
 * `routes/portal/index.tsx` IMPORTS `validateDashboardTabSearch` rather than
 * re-typing the list, so a tab cannot be legal in one half and illegal in the
 * other.
 *
 *   ?tab=buildings|exposure|record  → that tab
 *   ?tab absent or unrecognised     → "buildings"; an unrecognised param is
 *                                     DROPPED from the URL, not round-tripped
 *   selecting "buildings"           → writes `tab: undefined`, dropping `tab`
 *
 * THE DEFAULT IS RENDER-HALF ONLY: defaulting in `validateSearch` would stamp
 * `?tab=buildings` onto dozens of shipped `<Link to="/portal">` sites and end
 * the `tab`-less URL. "buildings" is the default because it is what `/portal`
 * rendered before the tab strip.
 *
 * DROPPING `tab` IS NOT A BARE `/portal`. This route defaults
 * `sortBy`/`sortDir` unconditionally (`routes/portal/index.tsx:59`), so
 * `/portal?tab=record` lands Buildings on
 * `/portal?sortBy=enforcement&sortDir=desc`, verified live: the bare URL must
 * key the query slot WelcomeWarmup prefetched. Do NOT strip the sort keys;
 * that re-sorts the table behind the reader on every return.
 *
 * NO ANCHOR MAP. The Building page resolves `#hash` to a tab for three shipped
 * links (`#managed-cases`, `#case-<id>` from the frozen TrackerSidebar,
 * `#applicable-codes` from `PortfolioHandledWins`); nothing in the app links
 * to an anchor on `/portal`, searched not assumed (every `hash:` in `src/` is
 * `BuildingDetail/tabs.ts` or a router-mock fixture). THE DAY ONE LANDS: an
 * anchor-to-tab map plus `tabForHash`, `resolveDashboardTab` taking a hash
 * argument AHEAD of `?tab=` (a hash names an ELEMENT, the more specific
 * request), and the route reading `useLocation({select: l => l.hash})` like
 * `routes/portal/buildings/$buildingId.tsx`.
 */

import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";

/** The three tabs the boards draw, in the order they draw them. */
export type DashboardTab = "buildings" | "exposure" | "record";

export const DASHBOARD_TABS: readonly DashboardTab[] = [
  "buildings",
  "exposure",
  "record",
] as const;

/** The tab strip's id; a tab swap scrolls it back into view. */
export const DASHBOARD_TABS_ID = "dashboard-tabs";

/** What a bare `/portal` renders. */
export const DEFAULT_DASHBOARD_TAB: DashboardTab = "buildings";

/** Tab-strip labels. Sentence case, per the boards. */
export const DASHBOARD_TAB_LABELS: Record<DashboardTab, string> = {
  buildings: "Buildings",
  exposure: "Exposure",
  record: "Record",
};

/** The ONE guard both halves read: a tab is legal in the URL exactly when it
 *  is legal in the render. */
export const isDashboardTab = (v: unknown): v is DashboardTab =>
  typeof v === "string" && (DASHBOARD_TABS as readonly string[]).includes(v);

/**
 * THE resolver: the only place the render half applies the default, which is
 * what stops a second `?? DEFAULT_DASHBOARD_TAB` appearing at a call site. See
 * the module docblock for where a hash argument would go.
 */
export const resolveDashboardTab = (tab: unknown): DashboardTab =>
  isDashboardTab(tab) ? tab : DEFAULT_DASHBOARD_TAB;

/** The render half's shape: a tab is always resolved. */
export interface DashboardSearch {
  tab: DashboardTab;
}

/**
 * Normalize raw route search for rendering. Exported separately (the
 * `readBuildingDetailSearch` idiom) so a unit test pins the contract without a
 * TanStack router. `useDashboardTab` resolves through this, so those tests
 * guard the default a real request takes, not a parallel copy.
 */
export const readDashboardSearch = (search: unknown): DashboardSearch => {
  const obj = (search ?? {}) as Record<string, unknown>;
  return { tab: resolveDashboardTab(obj.tab) };
};

/** The URL half's shape: an unrecognised tab is absent, not defaulted. */
export interface DashboardTabRouteSearch {
  tab?: DashboardTab;
}

/**
 * The `tab` half of the route's `validateSearch`, DELIBERATELY NOT THE WHOLE
 * VALIDATOR. `sortBy`/`sortDir` DO default in the URL half; `tab` must NOT.
 * Two opposite rules in one function is how one gets "made consistent" with
 * the other, so the route composes this in instead.
 */
export const validateDashboardTabSearch = (
  search?: Record<string, unknown>,
): DashboardTabRouteSearch => ({
  tab: isDashboardTab(search?.tab) ? (search.tab as DashboardTab) : undefined,
});

/**
 * The tab-strip counts, "Buildings 14". Stated ONLY when this route can answer
 * them.
 *
 * EXPOSURE CARRIES NO COUNT: "Exposure 5 agencies" was cut (Leo). "5" beside a
 * tab reads as a workload when it is five buckets of the same dollars.
 *
 * ABSENT IS NOT ZERO. No payload renders no count, never "Buildings 0", which
 * asserts an empty portfolio while the body below still shimmers. A REAL zero
 * IS allowed for `buildings`, since the empty state explains it; `exposure`
 * declines even a real zero, because "Exposure 0 agencies" is true both of
 * nothing owed and of a flag-off and reads as the first.
 *
 * NO `record` COUNT. The board draws "Record 95 MATTERS" but no total-matter
 * count is on the wire (`byAgency.rows` carries `owedCount` and
 * `notYoursCount`, the owed and third-party subsets, not the ledger's size),
 * and summing them would mint a second formula for a number the Record board's
 * PR will put on the wire.
 */
export const dashboardTabCounts = (
  buildingsCount: number | null | undefined,
): Partial<Record<DashboardTab, string>> => {
  const counts: Partial<Record<DashboardTab, string>> = {};
  if (typeof buildingsCount === "number" && Number.isFinite(buildingsCount)) {
    counts.buildings = `${buildingsCount}`;
  }
  return counts;
};

/**
 * Which tab is showing, and how to change it. Selecting the DEFAULT tab writes
 * `undefined`, DROPPING the param, so a URL with no `tab` keeps meaning the
 * dashboard. EVERYTHING ELSE ON THE QUERY STRING RIDES THROUGH the spread
 * below: `/portal` carries `sortBy`/`sortDir`, and a reader who sorted by
 * projection then clicked Record has to come BACK to their sort.
 */
export const useDashboardTab = (
  tab: DashboardTab | undefined,
): {
  activeTab: DashboardTab;
  /** Is the DEFAULT tab showing, i.e. the body a bare `/portal` renders. */
  isDefaultTab: boolean;
  goToTab: (next: DashboardTab) => void;
} => {
  const navigate = useNavigate();
  // One resolver, shared with the route's own read. Do not inline a second
  // `?? DEFAULT_DASHBOARD_TAB`: `tab` is typed as already-validated, which is
  // what tempts the next reader to drop the fallback.
  const { tab: activeTab } = readDashboardSearch({ tab });
  const goToTab = useCallback(
    (next: DashboardTab) => {
      navigate({
        to: "/portal",
        // Leo 2026-10-01: Back leaves the page, not the tab. Scroll stays put.
        replace: true,
        resetScroll: false,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          tab: next === DEFAULT_DASHBOARD_TAB ? undefined : next,
        }),
        // NO `hash` KEY. Nothing links to an anchor on `/portal` today, and
        // router-core reads an absent `hash` as CLEAR (`router.js:367`), the
        // behaviour the Building page relies on, so this stays correct the day
        // anchors arrive.
      });
    },
    [navigate],
  );
  return {
    activeTab,
    isDefaultTab: activeTab === DEFAULT_DASHBOARD_TAB,
    goToTab,
  };
};
